import React, { useState } from 'react';
import { formatDate } from './formatDate';
import { formatAnalyticalValue } from './formatValue';
import { icpDilutionLabel, type IcpXmlEntry } from './icpXmlData';
import { getResolvedValue } from './resolveColumns';
import type { ResolvedEntityMap } from './resolveColumns';
import type { ColumnDef } from '../config/viewsConfig';
import type { Entity } from '../types';

const ExpandableJsonCell: React.FC<{ value: string }> = ({ value }) => {
  const [expanded, setExpanded] = useState(false);
  const preview = value.length > 80 ? `${value.slice(0, 80)}…` : value;

  if (!expanded) {
    return (
      <span className="json-cell-collapsed">
        <code className="json-cell json-cell-inline json-cell-truncated">{preview}</code>
        <button className="json-cell-toggle" onClick={(e) => { e.stopPropagation(); setExpanded(true); }}>Show</button>
      </span>
    );
  }

  return (
    <div className="json-cell-expandable">
      <pre className="json-cell json-cell-expanded">{value}</pre>
      <button className="json-cell-toggle" onClick={(e) => { e.stopPropagation(); setExpanded(false); }}>Hide</button>
    </div>
  );
};

/**
 * Extract a labeled, formatted measurement date for every entry in an
 * icp_xml_data array. Entries without a parseable measurementDate are kept and
 * rendered with a "-" placeholder so the run count matches the detail-page
 * "Measurement Runs" table.
 */
function icpMeasurementEntries(value: unknown): { label: string; date: string }[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry: IcpXmlEntry, i) => ({
    label: icpDilutionLabel(entry, i),
    date: (entry?.measurementDate && formatDate(entry.measurementDate)) || '-',
  }));
}

export interface RenderCellOptions {
  onEntityClick?: (entityId: number) => void;
  onNavigate?: (path: string) => void;
  resolvedEntities?: ResolvedEntityMap;
}

/**
 * Walk a dot-separated path into a JSON blob, unwrapping { value: X } objects.
 */
function getExtractedValue(entity: Entity, col: ColumnDef): any {
  const extract = col.extract!;
  const blob = entity.attributes?.[extract.from];
  if (!blob || typeof blob !== 'object') return undefined;
  let current: any = blob;
  for (const segment of extract.path.split('.')) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[segment];
  }
  if (current != null && typeof current === 'object' && 'value' in current) {
    current = current.value;
  }
  return current;
}

/**
 * Extract a plain string value for a cell (for CSV export, no React nodes).
 */
export function getCellPlainValue(
  entity: Entity,
  col: ColumnDef,
  resolvedEntities: ResolvedEntityMap = new Map(),
): string {
  if (col.key === 'id') return entity.id != null ? String(entity.id) : '';

  if (col.key.includes('.')) {
    const value = getResolvedValue(entity, col, resolvedEntities);
    if (value === null || value === undefined) return '';
    if (col.render === 'date' && (typeof value === 'string' || typeof value === 'number')) return formatDate(value) || '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  if (col.extract) {
    const extracted = getExtractedValue(entity, col);
    if (col.render === 'text') return extracted == null ? '' : String(extracted);
    return formatAnalyticalValue(extracted);
  }

  if (col.render === 'icpMeasurementDates') {
    const entries = icpMeasurementEntries(entity.attributes?.[col.key]);
    if (entries.length === 0) return '';
    if (entries.length === 1) return entries[0].date;
    return entries.map(e => `${e.label}: ${e.date}`).join('; ');
  }

  const value = entity.attributes?.[col.key];
  if (value === null || value === undefined) return '';
  if (col.render === 'date') return formatDate(value) || '';
  if (col.render === 'number') return formatAnalyticalValue(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function renderCellValue(
  entity: Entity,
  col: ColumnDef,
  options: RenderCellOptions = {},
): React.ReactNode {
  const { onEntityClick, onNavigate, resolvedEntities = new Map() } = options;

  if (col.key === 'id') {
    return onEntityClick && entity.id ? (
      <span
        className="entity-id"
        onClick={(e) => { e.stopPropagation(); onEntityClick(entity.id!); }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEntityClick(entity.id!); }
        }}
      >
        {entity.id}
      </span>
    ) : (
      <span>{entity.id}</span>
    );
  }

  if (col.key.includes('.')) {
    const value = getResolvedValue(entity, col, resolvedEntities);
    if (value === null || value === undefined) {
      return <span className="null-value">-</span>;
    }
    if (col.render === 'date' && (typeof value === 'string' || typeof value === 'number')) return formatDate(value) || '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  if (col.extract) {
    const extracted = getExtractedValue(entity, col);
    if (extracted == null) return <span className="null-value">-</span>;
    if (col.render === 'text') return String(extracted);
    return formatAnalyticalValue(extracted);
  }

  const value = entity.attributes?.[col.key];

  if (col.render === 'icpMeasurementDates') {
    const entries = icpMeasurementEntries(value);
    if (entries.length === 0) return <span className="null-value">-</span>;
    if (entries.length === 1) return <span>{entries[0].date}</span>;
    return (
      <div className="icp-run-dates">
        {entries.map((e, i) => (
          <div key={i} className="icp-run-date">
            <span className="icp-run-label">{e.label}</span>
            <span className="icp-run-value">{e.date}</span>
          </div>
        ))}
      </div>
    );
  }

  if (value === null || value === undefined) {
    return <span className="null-value">-</span>;
  }

  if (col.render === 'number') {
    return formatAnalyticalValue(value);
  }

  if (col.render === 'link' && typeof value === 'number') {
    const targetType = col.key.replace(/_id$/, '');
    return (
      <span
        className="entity-id"
        onClick={onNavigate ? (e) => { e.stopPropagation(); onNavigate(`/${targetType}/${value}`); } : undefined}
        role="button"
        tabIndex={0}
        onKeyDown={onNavigate ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(`/${targetType}/${value}`); }
        } : undefined}
      >
        {value}
      </span>
    );
  }

  if (col.render === 'date') {
    return formatDate(value) || '-';
  }

  if (col.render === 'state' && typeof value === 'string') {
    const lower = value.toLowerCase();
    let cls = '';
    if (lower === 'completed') cls = 'fv-state-completed';
    else if (lower === 'in_progress' || lower === 'in progress') cls = 'fv-state-progress';
    else if (lower === 'cancelled' || lower === 'failed') cls = 'fv-state-cancelled';
    else cls = 'fv-state-default';
    return <span className={`fv-state-badge ${cls}`}>{value}</span>;
  }

  if (col.render === 'json' && typeof value === 'object') {
    return <ExpandableJsonCell value={JSON.stringify(value, null, 2)} />;
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return <ExpandableJsonCell value={JSON.stringify(value, null, 2)} />;

  return String(value);
}
